import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	OnDestroy,
	OnInit,
	output,
	signal,
	WritableSignal,
} from '@angular/core';
import { EntityChecklistItemComponent } from '../../../../shared/entity-workspace-components/entity-checklist-item/entity-checklist-item.component';
import { FormFieldLabelComponent } from '@app/shared/ui-components/form-field-label/form-field-label.component';
import { MatChipsModule } from '@angular/material/chips';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
	IAttachedIncident,
	IChecklistOption,
	IChecklistTemplate,
} from '@app/domain/types';
import { AttachedEntityCardComponent } from '@app/shared/entity-workspace-components/attached-entity-card/attached-entity-card.component';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { ChecklistService } from '@app/shared/services/checklist.service';
import { AttachedIncidentsService } from '@app/shared/services/attached-incidents.service';
import { IWorkOrder, IWorkOrderChecklist } from '@app/domain/cmms-types/workorder-types';
import {
	CdkDrag,
	CdkDragDrop,
	CdkDropList,
	moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
	AttachNewQuickEntityComponent,
	ActionType,
} from '@app/shared/entity-workspace-components/quick-entity/attach-new-quick-entity/attach-new-quick-entity.component';
import { MatMenuModule } from '@angular/material/menu';
import { NewRightSidebarService } from '@app/layout/new-right-sidebar/services/new-right-sidebar/new-right-sidebar.service';
import { OpenSidebarEventTypes } from '@app/layout/new-right-sidebar/types';
import { AttachedEntityService } from '@app/shared/services/attach-entity.service';

@Component({
	selector: 'tasks-tab',
	imports: [
		EntityChecklistItemComponent,
		FormFieldLabelComponent,
		MatChipsModule,
		NgxTolgeeModule,
		MatCardModule,
		MatButtonModule,
		MatIconModule,
		AttachedEntityCardComponent,
		CdkDrag,
		CdkDropList,
		AttachNewQuickEntityComponent,
		MatMenuModule,
	],
	templateUrl: './tasks-tab.component.html',
	styleUrl: './tasks-tab.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksTabComponent implements OnInit, OnDestroy {
	private readonly attachedIncidentsService = inject(AttachedIncidentsService);
	private readonly notify = inject(IconSnackBarService);
	private readonly translate = inject(TranslateService);
	private readonly checklistService = inject(ChecklistService);
	private readonly newRightSidebarService = inject(NewRightSidebarService);
	private readonly attachedEntityService = inject(AttachedEntityService);

	data = input<IWorkOrder>();
	updateData = output<Partial<IWorkOrder>>();

	readonly ActionType = ActionType;

	template = signal<IChecklistTemplate>(null);
	completed = computed<number>(
		() =>
			(this.template()?.options || []).filter(o => o.answer === false || o.answer).length
	);

	openIncidents = computed<number>(() => {
		return (
			this.data().incidentWorkOrders.edges.filter(edge => edge.node.status === 'OPEN')
				.length ?? 0
		);
	});
	openRequests = computed<number>(() => {
		return (
			this.data().requestWorkOrders.edges.filter(edge => edge.node.status === 'TODO')
				.length ?? 0
		);
	});

	entityPkList: WritableSignal<string[]> = signal<[]>([]);

	ngOnInit() {
		const checklist: IWorkOrderChecklist = this.data().checklists?.[0];
		this.template.set(
			checklist?.template?.length > 0
				? (JSON.parse(checklist.template) as IChecklistTemplate)
				: null
		);
	}

	updateChecklistOption(option: [string, number]) {
		if (!option) return;

		const [jsonOption, index] = option;
		const existingChecklist = this.data().checklists?.[0] ?? null;
		const parsedOption = JSON.parse(jsonOption) as IChecklistOption;

		const currentOptions = [...(this.template()?.options || [])];

		currentOptions[index] = parsedOption;

		this.template.set({
			...this.template(),
			options: currentOptions,
		});

		this.updateData.emit({
			checklists: [{ ...existingChecklist, template: JSON.stringify(this.template()) }],
		});
	}
	async addChecklist() {
		const updatedTemplate = await this.checklistService.addChecklist(this.template());
		if (updatedTemplate === this.template()) {
			return;
		}

		if (this.data().checklists?.[0]?.id) {
			this.checklistService.updateChecklist(
				JSON.stringify(updatedTemplate),
				this.data().checklists[0].id
			);
			this.template.set(updatedTemplate);
			this.notify.success(this.translate.instant('checklist_updated'));
			return;
		}
		this.updateData.emit({
			checklists: [
				{
					id: null,
					template: JSON.stringify({
						...updatedTemplate,
					}),
				},
			],
		});
		this.template.set(updatedTemplate);
		this.notify.success(this.translate.instant('checklist_added'));
	}

	deleteChecklist() {
		if (!this.data().checklists?.[0]?.id) {
			return;
		}
		this.checklistService.deleteChecklist(this.data().checklists[0].id);
		this.updateData.emit({
			checklists: [],
		});
	}

	selectionChanged([isChecked, entityData]: [boolean, IAttachedIncident]) {
		if (isChecked) {
			this.entityPkList.update(currentList => {
				return [...currentList, entityData.pk];
			});
			return;
		}
		this.entityPkList.set(this.entityPkList().filter(entity => entity !== entityData.pk));
	}

	//use when incident actions design is ready
	// Create a unlink function in backend so we can just send inn all ids?
	unlinkIncidents() {
		this.attachedIncidentsService.unlinkIncidentFromWorkorder(
			this.data().pk,
			this.entityPkList()
		);
	}

	drop(dropEvent: CdkDragDrop<IChecklistOption[]>) {
		const currentTemplate = this.template();
		const optionsCopy = [...currentTemplate.options];

		moveItemInArray(optionsCopy, dropEvent.previousIndex, dropEvent.currentIndex);

		this.template.set({
			...currentTemplate,
			options: optionsCopy,
		});

		this.updateData.emit({
			checklists: [{ template: JSON.stringify(this.template()) }],
		});
	}

	openAttachExistingEntities(entityType: 'incident' | 'request') {
		this.attachedEntityService.workorderData.set(this.data());
		this.newRightSidebarService.navigateAndOpenSupport({
			type: OpenSidebarEventTypes.AttachEntities,
			entityType: entityType,
		});
	}

	ngOnDestroy() {
		this.entityPkList.set([]);
	}
}
